/**
 * Authentication service for AWS Amplify Auth integration
 * Handles login, logout, password management, and session management
 *
 * @module authService
 */

import { signIn, signOut, fetchUserAttributes, updatePassword, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth'
import { logger } from '../utils/logger'
import type { User, LoginCredentials, ChangePasswordData, NewPasswordRequiredData } from '../types'

/**
 * Sign in user with email and password
 * @param credentials Login credentials (email and password)
 * @returns Promise resolving to authenticated user
 * @throws Error if authentication fails
 */
export async function signInUser(credentials: LoginCredentials): Promise<User> {
  logger.debug(`authService.signInUser: attempting login for email=${credentials.email}`)

  try {
    const signInResult = await signIn({
      username: credentials.email,
      password: credentials.password,
    })

    logger.debug(`authService.signInUser: sign in result`, { nextStep: signInResult.nextStep.signInStep })

    // If server requires new password, we need to handle that flow
    if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      logger.info(`authService.signInUser: NEW_PASSWORD_REQUIRED flow detected for ${credentials.email}`)
      // User needs to set a new password - throw error with special code
      throw new Error('NEW_PASSWORD_REQUIRED')
    }

    // Get authenticated user attributes
    const userAttributes = await fetchUserAttributes()
    logger.debug(`authService.signInUser: fetched user attributes for ${userAttributes.email}`)

    // Get groups from the ID token
    const session = await fetchAuthSession()
    const idTokenPayload = session.tokens?.idToken?.payload
    const groups = (idTokenPayload?.['cognito:groups'] as string[]) || []

    logger.debug(`authService.signInUser: user groups from token`, { groups })

    const user = mapUserAttributes(userAttributes, groups)
    logger.info(`authService.signInUser: user successfully authenticated - ${user.email}, role=${user.role}`)

    return user
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.error(`authService.signInUser: authentication failed for ${credentials.email}`, { error: errorMessage })
    throw error
  }
}

/**
 * Sign out the current user
 * Clears session and signs user out
 * @throws Error if sign out fails
 */
export async function signOutUser(): Promise<void> {
  logger.debug(`authService.signOutUser: attempting to sign out`)

  try {
    await signOut({ global: true })
    logger.info(`authService.signOutUser: user successfully signed out`)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.error(`authService.signOutUser: sign out failed`, { error: errorMessage })
    throw error
  }
}

/**
 * Get the currently authenticated user
 * @returns Promise resolving to current user or null if not authenticated
 */
export async function getCurrentUser(): Promise<User | null> {
  logger.debug(`authService.getCurrentUser: fetching current user`)

  try {
    const userAttributes = await fetchUserAttributes()
    logger.debug(`authService.getCurrentUser: current user is ${userAttributes.email}`)

    // Get groups from the ID token (not available in user attributes)
    const session = await fetchAuthSession()
    const idTokenPayload = session.tokens?.idToken?.payload
    const groups = (idTokenPayload?.['cognito:groups'] as string[]) || []

    logger.debug(`authService.getCurrentUser: user groups from token`, { groups })

    return mapUserAttributes(userAttributes, groups)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.debug(`authService.getCurrentUser: no authenticated user - ${errorMessage}`)
    return null
  }
}

/**
 * Change password for currently authenticated user
 * @param data Old and new password data
 * @throws Error if password change fails
 */
export async function changeUserPassword(data: ChangePasswordData): Promise<void> {
  logger.debug(`authService.changeUserPassword: attempting password change`)

  try {
    // Use Amplify's updatePassword function
    // TODO: Add password strength validation before submission
    await updatePassword({
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    })

    logger.info(`authService.changeUserPassword: password changed successfully`)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.error(`authService.changeUserPassword: password change failed`, { error: errorMessage })

    // Provide user-friendly error messages
    if (errorMessage.includes('Incorrect')) {
      throw new Error('Current password is incorrect')
    }
    if (errorMessage.includes('password')) {
      throw new Error('New password does not meet requirements')
    }
    throw error
  }
}

/**
 * Complete new password requirement (first login flow)
 * Used when user is in NEW_PASSWORD_REQUIRED state after initial invite
 * @param data New password and email for confirmation
 * @throws Error if password completion fails
 */
export async function completeNewPasswordRequired(data: NewPasswordRequiredData): Promise<User> {
  logger.debug(`authService.completeNewPasswordRequired: completing new password flow for ${data.email}`)

  try {
    // Use confirmSignIn to complete the NEW_PASSWORD_REQUIRED challenge
    // This is called after signIn returns the challenge
    // TODO: Add support for additional user attributes during this flow
    const confirmResult = await confirmSignIn({
      challengeResponse: data.newPassword,
    })

    logger.debug(`authService.completeNewPasswordRequired: confirm result`, {
      isSignedIn: confirmResult.isSignedIn,
      nextStep: confirmResult.nextStep?.signInStep,
    })

    if (!confirmResult.isSignedIn) {
      logger.error(`authService.completeNewPasswordRequired: sign in not completed after password set`)
      throw new Error('Failed to complete sign in after setting new password')
    }

    // Fetch the user attributes now that we're signed in
    const userAttributes = await fetchUserAttributes()
    logger.debug(`authService.completeNewPasswordRequired: fetched user attributes for ${userAttributes.email}`)

    // Get groups from the ID token
    const session = await fetchAuthSession()
    const idTokenPayload = session.tokens?.idToken?.payload
    const groups = (idTokenPayload?.['cognito:groups'] as string[]) || []

    const user = mapUserAttributes(userAttributes, groups)
    logger.info(`authService.completeNewPasswordRequired: new password set successfully for ${user.email}`)

    return user
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.error(`authService.completeNewPasswordRequired: failed to set new password`, { error: errorMessage })

    // Provide user-friendly error messages
    if (errorMessage.includes('password')) {
      throw new Error('Password does not meet requirements (12+ chars, uppercase, lowercase, numbers, symbols)')
    }
    throw error
  }
}

/**
 * Check if current user is in admin group
 * @returns Promise resolving to true if user is admin
 */
export async function isAdmin(): Promise<boolean> {
  logger.debug(`authService.isAdmin: checking admin status`)

  try {
    // Get groups from the ID token (not available in user attributes)
    const session = await fetchAuthSession()
    const idTokenPayload = session.tokens?.idToken?.payload
    const groups = (idTokenPayload?.['cognito:groups'] as string[]) || []
    const isUserAdmin = groups.includes('admin') || groups.includes('moderator')

    logger.debug(`authService.isAdmin: user admin status=${isUserAdmin}`, { groups })
    return isUserAdmin
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.warn(`authService.isAdmin: could not determine admin status`, { error: errorMessage })
    return false
  }
}

/**
 * Get current session tokens for API requests
 * @returns Promise resolving to session tokens object or null
 */
export async function getSession(): Promise<{ accessToken?: string; idToken?: string } | null> {
  logger.debug(`authService.getSession: getting current session`)

  try {
    // Fetch the current auth session which includes tokens
    const session = await fetchAuthSession()

    if (!session.tokens) {
      logger.debug(`authService.getSession: no tokens in session`)
      return null
    }

    const accessToken = session.tokens.accessToken?.toString()
    const idToken = session.tokens.idToken?.toString()

    logger.debug(`authService.getSession: session tokens retrieved`, {
      hasAccessToken: !!accessToken,
      hasIdToken: !!idToken,
    })

    // TODO: Add token expiry checking and automatic refresh
    // TODO: Implement secure token storage if needed for offline scenarios

    return {
      accessToken,
      idToken,
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    logger.debug(`authService.getSession: no active session`, { error: errorMessage })
    return null
  }
}

/**
 * Map AWS Cognito user attributes to application User type
 * @param attributes Raw Cognito user attributes
 * @param groups User's Cognito groups (from ID token)
 * @returns Mapped User object
 */
function mapUserAttributes(attributes: Record<string, any>, groups: string[] = []): User {
  logger.debug(`mapUserAttributes: mapping user attributes for ${attributes.email}`, { groups })

  const role = groups.includes('admin') ? 'admin' : 'member'

  const user: User = {
    id: attributes.sub,
    email: attributes.email,
    displayName: attributes.name || attributes.email,
    role,
    isEnabled: true,
    createdAt: attributes.created_at || new Date().toISOString(),
    lastLogin: attributes.last_authenticated || undefined,
  }

  logger.debug(`mapUserAttributes: mapped user`, { user, role, groups })
  return user
}
